import { useState } from "react";

import { useOverrideImpact } from "../api/useOverrideImpact";
import type { DraftBody } from "../mappers";
import { getAffectedRows, OVERRIDE_STATE, toOverrideRows } from "../overrides";

import type { VacancyOverride } from "@/features/segments/types";

type UseSegmentOverridesParams = {
  segmentId?: string;
  draftBody: DraftBody;
  overrides: VacancyOverride[];
};

export function useSegmentOverrides({
  segmentId,
  draftBody,
  overrides,
}: UseSegmentOverridesParams) {
  const impact = useOverrideImpact(segmentId, draftBody);
  const [kept, setKept] = useState<ReadonlySet<number>>(new Set());

  const rows = toOverrideRows(impact.data ?? [], overrides);
  const affected = getAffectedRows(rows, kept);
  const redundant = rows.filter(
    (row) => row.state === OVERRIDE_STATE.REDUNDANT,
  );

  return {
    rows,
    affected,
    redundant,
    kept,
    isKept: (vacancyId: number) => kept.has(vacancyId),
    keep: (vacancyId: number) =>
      setKept((previous) => new Set(previous).add(vacancyId)),
    isLoading: impact.isLoading,
    isFetching: impact.isFetching,
    isResolved: impact.data !== undefined,
  };
}

export type SegmentOverrides = ReturnType<typeof useSegmentOverrides>;
